/**
 * SICard — compact list row for a single Supplemental Instruction.
 * Follows RFICard pattern.
 */
import { SI, SI_STATUS_CONFIG } from '@/types/supplemental_instructions';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trash2, FileText } from 'lucide-react';

interface SICardProps {
  si: SI;
  onClick: (si: SI) => void;
  onDelete: (siId: string) => void;
}

function formatDate(val: string | null | undefined) {
  if (!val) return null;
  return new Date(val).toLocaleDateString();
}

export function SICard({ si, onClick, onDelete }: SICardProps) {
  const statusConfig = SI_STATUS_CONFIG[si.status] ?? { label: si.status, className: 'bg-gray-200 text-gray-800' };

  return (
    <div
      className="flex items-start justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={() => onClick(si)}
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <FileText className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono text-muted-foreground">{si.si_number}</span>
            <Badge className={statusConfig.className + ' text-xs'}>{statusConfig.label}</Badge>
            {si.rfi_id && (
              <span className="text-xs text-muted-foreground">from RFI</span>
            )}
          </div>
          <p className="text-sm font-medium truncate mt-0.5">{si.title}</p>
          <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
            {si.issued_by && <span>Issued by {si.issued_by}</span>}
            {si.issued_date && <span>{formatDate(si.issued_date)}</span>}
            {si.drawing_references && <span>Dwg: {si.drawing_references}</span>}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 ml-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(si.id);
          }}
          title="Delete SI"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}
